import React, { lazy } from 'react';

import { Route } from 'react-router-dom';

import { routes, type AmlRouteName, type OnboardingRouteName } from '@app/constants/route.constants';

const ApplicationNotFoundPage = lazy(() => import('./features/ApplicationNotFoundPage'));
const ProductHighInterestAccountHuseierne = lazy(() => import('./features/ProductPages/FixedDepositAccountHuseierne'));
const LoginPage = lazy(() => import('./features/LoginPage'));
const CreateApplicationPage = lazy(() => import('./features/CreateApplicationPage'));
const OngoingApplicationsPage = lazy(() => import('./features/OngoingApplicationsPage'));
const CardAndAccountStandardPage = lazy(() => import('./features/ProductPages/CardAndAccount/Standard'));
const CardAndAccountFramPage = lazy(() => import('./features/ProductPages/CardAndAccount/Fram'));
const CardAndAccountPrivateBankingPage = lazy(() => import('./features/ProductPages/CardAndAccount/PrivateBanking'));
const ProductStandardPackageProductSelectionPage = lazy(
  () => import('./features/ProductPages/ProductSelection/StandardPackageProductSelection')
);
const ProductPrivateBankingIntroPage = lazy(() => import('./features/ProductPages/PrivateBanking'));
const ProductPrivateBankingProductSelectionPage = lazy(
  () => import('./features/ProductPages/ProductSelection/PrivateBankingProductSelection')
);
const ProductFramMembershipPage = lazy(() => import('./features/ProductPages/FramMembership'));
const ProductFramMultiSelectionPage = lazy(
  () => import('./features/ProductPages/ProductSelection/FramProductSelection')
);
const ProductSummaryFramPage = lazy(() => import('@features/ProductPages/ProductOrderSummary/FramSummary'));
const ProductSummaryStandardPage = lazy(() => import('@features/ProductPages/ProductOrderSummary/StandardSummary'));
const ProductSummaryPrivateBankingPage = lazy(
  () => import('@features/ProductPages/ProductOrderSummary/PrivateBankingSummary')
);
const ConsentPagesPrivacy = lazy(() => import('./features/ConsentPages'));
const ConsentPagesDigitalCustomerRelation = lazy(() => import('./features/ConsentPages/DigitalCustomerRelation'));
const ConsentPagesDigitalMarketing = lazy(() => import('./features/ConsentPages/DigitalMarketing'));
const ConsentPagesPersonalData = lazy(() => import('./features/ConsentPages/PersonalData'));
const SigningIntroductionPage = lazy(() => import('./features/SigningPages/SingleProductSigning'));
const SigningReceiptPage = lazy(() => import('./features/SigningPages/Receipt'));
const ContinueProcessRedirector = lazy(() => import('./features/ContinueProcessRedirector'));
const ManualApplicationStatusPage = lazy(() => import('./features/SigningPages/ManualApplicationStatusPage'));
const HuseierneAccountAlreadyExistsPage = lazy(() => import('./features/HuseierneAccountAlreadyExistsPage'));
const QuestionCustomerInformationOverviewPage = lazy(
  () => import('./features/ContactInformationAndCitizenshipsOverview')
);
const QuestionOtherTaxLiabilitiesPage = lazy(() => import('./features/AMLQuestions/OtherTaxLiability'));
const QuestionOtherTaxLiabilitiesYesNoPage = lazy(() => import('./features/AMLQuestions/OtherTaxLiability/YesNo'));
const QuestionOtherTaxLiabilitiesCountrySelectPage = lazy(
  () => import('./features/AMLQuestions/OtherTaxLiability/CountrySelect')
);
const QuestionAmericanTaxLiabilityPage = lazy(() => import('./features/AMLQuestions/AmericanTaxLiability'));
const QuestionJobstatusPage = lazy(() => import('./features/AMLQuestions/Jobstatus'));
const QuestionMaritalStatusPage = lazy(() => import('./features/AMLQuestions/MaritalStatus'));
const QuestionBankingPurposePage = lazy(() => import('./features/AMLQuestions/BankingPurpose'));
const QuestionCrypto = lazy(() => import('./features/AMLQuestions/Crypto'));
const QuestionPoliticallyExposedPersonPage = lazy(() => import('./features/AMLQuestions/PoliticallyExposedPerson'));
const QuestionMainIncomePage = lazy(() => import('./features/AMLQuestions/MainIncome'));
const QuestionBankingOnBehalfOfOthers = lazy(() => import('./features/AMLQuestions/BankingOnBehalfOfOthers'));
const QuestionCashTransactionsOutPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsOut')
);
const QuestionCashTransactionsOutSelectedAndSumPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsOut/SelectedAndSum')
);

const QuestionCashTransactionsOutTransferReasonsPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsOut/TransferReasons')
);

const QuestionCashTransactionsInPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsIn')
);

const QuestionCashTransactionsInSelectedAndSumPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsIn/SelectedAndSum')
);

const QuestionCashTransactionsInTransferReasonsPage = lazy(
  () => import('./features/AMLQuestions/CashTransactions/CashTransactionsIn/TransferReasons')
);

const QuestionInternationalTransferPage = lazy(() => import('./features/AMLQuestions/InternationalTransfers'));
const QuestionInternationalTransferYesNoPage = lazy(
  () => import('./features/AMLQuestions/InternationalTransfers/YesNo')
);
const QuestionInternationalTransferCountrySelectPage = lazy(
  () => import('./features/AMLQuestions/InternationalTransfers/CountrySelect')
);
const QuestionInternationalTransferCountryDetailsPage = lazy(
  () => import('./features/AMLQuestions/InternationalTransfers/CountryDetails')
);
const QuestionIntroPage = lazy(() => import('./features/AMLQuestions/Intro'));
const QuestionOutroPage = lazy(() => import('./features/AMLQuestions/Outro'));

const amlPages: Record<
  Exclude<
    AmlRouteName,
    | 'QuestionSendTransferCountrySelect'
    | 'QuestionSendTransferCountryDetails'
    | 'QuestionReceiveTransferCountrySelect'
    | 'QuestionReceiveTransferCountryDetails'
    | 'QuestionOtherTaxLiabilitiesCountrySelect'
    | 'QuestionCashTransactionsOutPurpose'
    | 'QuestionCashTransactionsInSource'
  >,
  React.ReactElement
> = {
  QuestionIntro: <Route path={routes.QuestionIntro} element={<QuestionIntroPage />} />,
  QuestionAmericanTaxLiability: (
    <Route path={routes.QuestionAmericanTaxLiability} element={<QuestionAmericanTaxLiabilityPage />} />
  ),
  QuestionOtherTaxLiabilities: (
    <Route path={routes.QuestionOtherTaxLiabilities} element={<QuestionOtherTaxLiabilitiesPage />}>
      <Route index element={<QuestionOtherTaxLiabilitiesYesNoPage />} />
      <Route path="land" element={<QuestionOtherTaxLiabilitiesCountrySelectPage />} />
    </Route>
  ),
  QuestionJobstatus: <Route path={routes.QuestionJobstatus} element={<QuestionJobstatusPage />} />,
  QuestionMaritalstatus: <Route path={routes.QuestionMaritalstatus} element={<QuestionMaritalStatusPage />} />,
  QuestionBankingPurpose: <Route path={routes.QuestionBankingPurpose} element={<QuestionBankingPurposePage />} />,
  QuestionCrypto: <Route path={routes.QuestionCrypto} element={<QuestionCrypto />} />,
  QuestionPep: <Route path={routes.QuestionPep} element={<QuestionPoliticallyExposedPersonPage />} />,
  QuestionMainIncome: <Route path={routes.QuestionMainIncome} element={<QuestionMainIncomePage />} />,
  QuestionBankingOnBehalfOfOthers: (
    <Route path={routes.QuestionBankingOnBehalfOfOthers} element={<QuestionBankingOnBehalfOfOthers />} />
  ),
  QuestionCashTransactionsOut: (
    <Route path={routes.QuestionCashTransactionsOut} element={<QuestionCashTransactionsOutPage />}>
      <Route index element={<QuestionCashTransactionsOutSelectedAndSumPage />} />
      <Route path="hensikt" element={<QuestionCashTransactionsOutTransferReasonsPage />} />
    </Route>
  ),
  QuestionCashTransactionsIn: (
    <Route path={routes.QuestionCashTransactionsIn} element={<QuestionCashTransactionsInPage />}>
      <Route index element={<QuestionCashTransactionsInSelectedAndSumPage />} />
      <Route path="kilde" element={<QuestionCashTransactionsInTransferReasonsPage />} />
    </Route>
  ),
  QuestionSendTransfer: (
    <Route path={routes.QuestionSendTransfer} element={<QuestionInternationalTransferPage direction="send" />}>
      <Route index element={<QuestionInternationalTransferYesNoPage />} />
      <Route path="land" element={<QuestionInternationalTransferCountrySelectPage />} />
      <Route path="land/:country" element={<QuestionInternationalTransferCountryDetailsPage />} />
    </Route>
  ),
  QuestionReceiveTransfer: (
    <Route path={routes.QuestionReceiveTransfer} element={<QuestionInternationalTransferPage direction="receive" />}>
      <Route index element={<QuestionInternationalTransferYesNoPage />} />
      <Route path="land" element={<QuestionInternationalTransferCountrySelectPage />} />
      <Route path="land/:country" element={<QuestionInternationalTransferCountryDetailsPage />} />
    </Route>
  ),
  QuestionOutro: <Route path={routes.QuestionOutro} element={<QuestionOutroPage />} />,
} as const;

const loggedInPagesWithoutUid: Record<
  Extract<
    OnboardingRouteName,
    'CreateApplicationPage' | 'OngoingApplicationsPage' | 'HuseierneAccountAlreadyExistsPage'
  >,
  React.ReactElement
> = {
  CreateApplicationPage: <Route path={routes.CreateApplicationPage} element={<CreateApplicationPage />} />,
  OngoingApplicationsPage: <Route path={routes.OngoingApplicationsPage} element={<OngoingApplicationsPage />} />,
  HuseierneAccountAlreadyExistsPage: (
    <Route path={routes.HuseierneAccountAlreadyExistsPage} element={<HuseierneAccountAlreadyExistsPage />} />
  ),
} as const;

const loggedInOnboardingPagesWithUid: Record<
  Exclude<
    OnboardingRouteName,
    'CreateApplicationPage' | 'OngoingApplicationsPage' | 'HuseierneAccountAlreadyExistsPage' | 'LoginPage'
  >,
  React.ReactElement
> = {
  QuestionContactInformationAndCitizenshipsOverview: (
    <Route
      path={routes.QuestionContactInformationAndCitizenshipsOverview}
      element={<QuestionCustomerInformationOverviewPage />}
    />
  ),
  LoginPageWithUid: <Route path={routes.LoginPageWithUid} element={<LoginPage />} />,
  ContinueProcessRedirector: <Route path={routes.ContinueProcessRedirector} element={<ContinueProcessRedirector />} />,
  CardAndAccountStandardPage: (
    <Route path={routes.CardAndAccountStandardPage} element={<CardAndAccountStandardPage />} />
  ),
  CardAndAccountFramPage: <Route path={routes.CardAndAccountFramPage} element={<CardAndAccountFramPage />} />,
  CardAndAccountPrivateBankingPage: (
    <Route path={routes.CardAndAccountPrivateBankingPage} element={<CardAndAccountPrivateBankingPage />} />
  ),
  ProductOverviewStandardProductSelect: (
    <Route
      path={routes.ProductOverviewStandardProductSelect}
      element={<ProductStandardPackageProductSelectionPage />}
    />
  ),
  ProductOverviewPrivateBankingIntro: (
    <Route path={routes.ProductOverviewPrivateBankingIntro} element={<ProductPrivateBankingIntroPage />} />
  ),
  ProductOverviewPrivateBankingProductSelect: (
    <Route
      path={routes.ProductOverviewPrivateBankingProductSelect}
      element={<ProductPrivateBankingProductSelectionPage />}
    />
  ),
  ProductOverviewHighInterestAccountHuseierne: (
    <Route
      path={routes.ProductOverviewHighInterestAccountHuseierne}
      element={<ProductHighInterestAccountHuseierne />}
    />
  ),
  ConsentPrivacy: <Route path={routes.ConsentPrivacy} element={<ConsentPagesPrivacy />} />,
  ConsentDigitalCustomerRelation: (
    <Route path={routes.ConsentDigitalCustomerRelation} element={<ConsentPagesDigitalCustomerRelation />} />
  ),
  ConsentDigitalMarketing: <Route path={routes.ConsentDigitalMarketing} element={<ConsentPagesDigitalMarketing />} />,
  ConsentPersonalData: <Route path={routes.ConsentPersonalData} element={<ConsentPagesPersonalData />} />,
  SignDocuments: <Route path={routes.SignDocuments} element={<SigningIntroductionPage />} />,
  SignDocumentsReceipt: <Route path={routes.SignDocumentsReceipt} element={<SigningReceiptPage />} />,
  ManualApplicationStatus: <Route path={routes.ManualApplicationStatus} element={<ManualApplicationStatusPage />} />,
  ApplicationNotFoundPage: <Route path={routes.ApplicationNotFoundPage} element={<ApplicationNotFoundPage />} />,
  ProductOverviewFramMembership: (
    <Route path={routes.ProductOverviewFramMembership} element={<ProductFramMembershipPage />} />
  ),
  ProductOverviewFramProductSelect: (
    <Route path={routes.ProductOverviewFramProductSelect} element={<ProductFramMultiSelectionPage />} />
  ),
  ProductSummaryFram: <Route path={routes.ProductSummaryFram} element={<ProductSummaryFramPage />} />,
  ProductSummaryStandard: <Route path={routes.ProductSummaryStandard} element={<ProductSummaryStandardPage />} />,

  ProductSummaryPrivateBanking: (
    <Route path={routes.ProductSummaryPrivateBanking} element={<ProductSummaryPrivateBankingPage />} />
  ),
} as const;

export const openPageWithProductRouteObjects: React.ReactElement[] = [
  <Route key="login" path={routes.LoginPage} element={<LoginPage />} />,
];

export const loggedInPageWithUidRouteObjects: React.ReactElement[] = Object.entries({
  ...amlPages,
  ...loggedInOnboardingPagesWithUid,
}).map(([name, route]: [string, React.ReactElement]) => React.cloneElement(route, { key: name }));

export const loggedInPageWithoutUidRouteObjects: React.ReactElement[] = Object.entries(loggedInPagesWithoutUid).map(
  ([name, route]: [string, React.ReactElement]) => React.cloneElement(route, { key: name })
);
